import { useState, useRef, useEffect } from "react";
import { Search, MapPin, Check, Loader2, Store, ExternalLink } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { useDebounce } from "@/hooks/useDebounce";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

interface LocalRestaurant {
  id: string;
  name: string;
  address: string | null;
  city: string;
  state: string | null;
  category: string | null;
  googlePlaceId: string | null;
  brandCategoryName?: string | null;
  kudozExternalId?: number | null;
  logoImageUrl?: string | null;
  primaryImageUrl?: string | null;
  googlePhotoName?: string | null;
}

interface GooglePlaceResult {
  placeId: string;
  name: string;
  address: string;
  city: string;
  state: string;
  category: string | null;
  rating: number | null;
}

interface SearchResults {
  local: LocalRestaurant[];
  google: GooglePlaceResult[];
}

interface SelectedRestaurant {
  id: string | null;
  name: string;
  address: string;
  city: string;
  state: string;
  googlePlaceId: string | null;
  isNew: boolean;
  category: string | null;
  brandCategoryName?: string | null;
  kudozExternalId?: number | null;
  logoImageUrl?: string | null;
  primaryImageUrl?: string | null;
  googlePhotoName?: string | null;
}

interface RestaurantSearchInputProps {
  value: string;
  onChange: (name: string) => void;
  onRestaurantSelect: (restaurant: SelectedRestaurant | null) => void;
  selectedRestaurant: SelectedRestaurant | null;
  userLatitude?: number;
  userLongitude?: number;
  className?: string;
  disabled?: boolean;
}

export function RestaurantSearchInput({
  value,
  onChange,
  onRestaurantSelect,
  selectedRestaurant,
  userLatitude,
  userLongitude,
  className,
  disabled = false,
}: RestaurantSearchInputProps) {
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [results, setResults] = useState<SearchResults>({ local: [], google: [] });
  const [focused, setFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const debouncedQuery = useDebounce(value, 300);

  useEffect(() => {
    const searchRestaurants = async () => {
      if (!debouncedQuery || debouncedQuery.length < 2) {
        setResults({ local: [], google: [] });
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        const params = new URLSearchParams({ query: debouncedQuery });
        if (userLatitude) params.append('latitude', userLatitude.toString());
        if (userLongitude) params.append('longitude', userLongitude.toString());

        const response = await fetch(`/api/restaurants/search?${params}`);
        if (response.ok) {
          const data = await response.json();
          setResults(data);
        }
      } catch (error) {
        console.error('Restaurant search failed:', error);
      } finally {
        setIsLoading(false);
      }
    };

    searchRestaurants();
  }, [debouncedQuery, userLatitude, userLongitude]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectLocal = (restaurant: LocalRestaurant) => {
    onRestaurantSelect({
      id: restaurant.id,
      name: restaurant.name,
      address: restaurant.address || '',
      city: restaurant.city,
      state: restaurant.state || '',
      googlePlaceId: restaurant.googlePlaceId,
      isNew: false,
      category: restaurant.category,
      brandCategoryName: restaurant.brandCategoryName,
      kudozExternalId: restaurant.kudozExternalId,
      logoImageUrl: restaurant.logoImageUrl || null,
      primaryImageUrl: restaurant.primaryImageUrl || null,
      googlePhotoName: restaurant.googlePhotoName || null,
    });
    onChange(restaurant.name);
    setIsOpen(false);
  };

  const handleSelectGoogle = async (place: GooglePlaceResult) => {
    setIsCreating(true);
    try {
      const response = await apiRequest('POST', '/api/restaurants/from-google-place', {
        placeId: place.placeId,
        createIfNotExists: true,
      });

      const data = await response.json();
      
      if (data.restaurant) {
        onRestaurantSelect({
          id: data.restaurant.id,
          name: data.restaurant.name,
          address: data.restaurant.address || place.address,
          city: data.restaurant.city || place.city,
          state: data.restaurant.state || place.state,
          googlePlaceId: place.placeId,
          isNew: data.created,
          category: data.restaurant.category || place.category,
        });
        onChange(data.restaurant.name);
      } else {
        toast({
          title: "Restaurant not found",
          description: "Could not load details for that location. Please try a different result.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Failed to create restaurant from Google Place:', error);
      toast({
        title: "Could not select restaurant",
        description: "Something went wrong while loading that restaurant. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsCreating(false);
      setIsOpen(false);
    }
  };

  const handleClear = () => {
    onChange('');
    onRestaurantSelect(null);
    setResults({ local: [], google: [] });
    setIsOpen(false);
    inputRef.current?.focus();
  };

  const filteredGoogleResults = results.google.filter(g => !results.local.some(l => l.googlePlaceId === g.placeId));
  const hasResults = results.local.length > 0 || results.google.length > 0;
  const showDropdown = isOpen && (hasResults || isLoading) && value.length >= 2;

  // When a restaurant is selected, show it as a selection state (not editable input)
  const hasValidSelection = !!selectedRestaurant?.id;

  return (
    <div className={cn("relative", className)}>
      {hasValidSelection ? (
        <div className="p-3 bg-muted border border-primary/20 rounded-md">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Check className="w-4 h-4 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-medium text-sm">{selectedRestaurant.name}</span>
                {selectedRestaurant.isNew && (
                  <Badge variant="secondary" className="text-xs">New</Badge>
                )}
              </div>
              <div className="flex items-center gap-1.5 mt-1">
                <MapPin className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                <p className="text-xs text-muted-foreground">
                  {selectedRestaurant.address && `${selectedRestaurant.address}, `}
                  {selectedRestaurant.city}{selectedRestaurant.state && `, ${selectedRestaurant.state}`}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={handleClear}
              className="text-xs text-primary hover:text-primary/80 font-medium"
              data-testid="button-change-restaurant"
            >
              Change
            </button>
          </div>
        </div>
      ) : (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            ref={inputRef}
            value={value}
            onChange={(e) => {
              const newValue = e.target.value;
              onChange(newValue);
              setIsOpen(newValue.length >= 2);
            }}
            onFocus={() => {
              setFocused(true);
              if (value.length >= 2) {
                setIsOpen(true);
              }
            }}
            onBlur={() => {
              // Delay blur to allow click events on dropdown items to fire first
              setTimeout(() => setFocused(false), 150);
            }}
            placeholder="Search by name or location…"
            className="pl-10 pr-10"
            disabled={disabled || isCreating}
            data-testid="input-restaurant-search"
          />
          {(isLoading || isCreating) && (
            <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />
          )}
        </div>
      )}
      
      {!hasValidSelection && value.length >= 2 && !isLoading && !hasResults && (
        <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1.5">
          <MapPin className="w-3 h-3" />
          Please select a restaurant from the search results
        </p>
      )}

      {showDropdown && (
        <div
          ref={dropdownRef}
          className="absolute z-50 top-full mt-1 w-full bg-popover border rounded-md shadow-lg max-h-80 overflow-y-auto"
        >
          {isLoading ? (
            <div className="p-4 text-center text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin mx-auto mb-2" />
              Searching...
            </div>
          ) : (
            <>
              {results.local.length > 0 && (
                <div>
                  <div className="px-3 py-2 text-xs font-medium text-muted-foreground bg-muted/50 sticky top-0">
                    <Store className="w-3 h-3 inline-block mr-1.5" />
                    In our database
                  </div>
                  {results.local.map((restaurant) => (
                    <button
                      key={restaurant.id}
                      type="button"
                      onClick={() => handleSelectLocal(restaurant)}
                      className="w-full px-3 py-2 text-left hover-elevate flex items-start gap-2"
                    >
                      <MapPin className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm truncate">{restaurant.name}</div>
                        <div className="text-xs text-muted-foreground truncate">
                          {restaurant.address && `${restaurant.address}, `}
                          {restaurant.city}{restaurant.state && `, ${restaurant.state}`}
                        </div>
                        {restaurant.category && (
                          <Badge variant="secondary" className="text-xs mt-1">
                            {restaurant.category}
                          </Badge>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {filteredGoogleResults.length > 0 && (
                <div>
                  <div className="px-3 py-2 text-xs font-medium text-muted-foreground bg-muted/50 sticky top-0 flex items-center gap-1.5">
                    <ExternalLink className="w-3 h-3" />
                    From Google
                  </div>
                  {filteredGoogleResults.map((place) => (
                    <button
                      key={place.placeId}
                      type="button"
                      onClick={() => handleSelectGoogle(place)}
                      className="w-full px-3 py-2 text-left hover-elevate flex items-start gap-2"
                      disabled={isCreating}
                    >
                      <MapPin className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm truncate">{place.name}</div>
                        <div className="text-xs text-muted-foreground truncate">
                          {place.address}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          {place.category && (
                            <Badge variant="outline" className="text-xs">
                              {place.category}
                            </Badge>
                          )}
                          {place.rating && (
                            <span className="text-xs text-muted-foreground">
                              {place.rating.toFixed(1)} rating
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {!hasResults && value.length >= 2 && (
                <div className="p-4 text-center text-sm text-muted-foreground">
                  No restaurants found for "{value}"
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export type { SelectedRestaurant };
